import {
  faChevronRight,
  faCircleCheck,
  faFile,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { AiProcessingItem } from '@/api/ai/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useTranslator } from '@/hooks/useTranslator';
import { SectionIcon } from './SectionIcon';

// Static asset-type → label key maps. Explicit literals (never a dynamic
// `jobType.${assetType}` key) so the strings are statically analysable.
const JOB_TYPE_LABEL_KEYS: Record<string, string> = {
  AUDIT: 'popover.jobType.audit',
  CERTIFICATE: 'popover.jobType.certificate',
  COMPANY_DOCUMENTS: 'popover.jobType.companyDocuments',
  TRACING: 'popover.jobType.tracing',
  CERTIFIED_MATERIALS: 'popover.jobType.certifiedMaterials',
};

const READY_TYPE_LABEL_KEYS: Record<string, string> = {
  AUDIT: 'popover.readyType.audit',
  CERTIFICATE: 'popover.readyType.certificate',
  COMPANY_DOCUMENTS: 'popover.readyType.companyDocuments',
  TRACING: 'popover.readyType.tracing',
  CERTIFIED_MATERIALS: 'popover.readyType.certifiedMaterials',
};

// Audit jobs don't always set `assetName`, so fall back to `metadata.file.name`.
function readFileName(item: AiProcessingItem): string {
  if (item.assetName) return item.assetName;
  const file = (item.metadata as Record<string, unknown> | null)?.file as
    | { name?: string }
    | undefined;
  return file?.name ?? '';
}

interface ActiveJobsPopoverProps {
  jobsTotal: number;
  runningCount: number;
  readyCount: number;
  inProgressItems: AiProcessingItem[];
  readyItems: AiProcessingItem[];
  onOpenQueue: () => void;
  children: ReactNode;
}

export function ActiveJobsPopover({
  jobsTotal,
  runningCount,
  readyCount,
  inProgressItems,
  readyItems,
  onOpenQueue,
  children,
}: ActiveJobsPopoverProps) {
  const { getTranslation } = useTranslator({ page: 'aiProcessing' });
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const show = () => {
    cancelClose();
    setOpen(true);
  };
  // Grace delay so moving the pointer from the button onto the popover content
  // doesn't dismiss it.
  const scheduleHide = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  useEffect(() => cancelClose, []);

  const summaryLine = [
    getTranslation('popover.jobsCount', { count: jobsTotal }),
    getTranslation('popover.running', { count: runningCount }),
    getTranslation('popover.ready', { count: readyCount }),
  ].join(' · ');

  const handleOpenQueue = () => {
    setOpen(false);
    onOpenQueue();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        // The rendered element is a <span> hover affordance, not a native
        // button (the real button is `children`), so opt out of Base UI's
        // native-button semantics to avoid its `nativeButton` warning.
        nativeButton={false}
        render={
          // biome-ignore lint/a11y/noStaticElementInteractions: mouse-only hover affordance; the interactive trigger is the child element and click/keyboard are handled by PopoverTrigger
          <span
            className='inline-flex'
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
          >
            {children}
          </span>
        }
      />
      <PopoverContent
        align='end'
        side='bottom'
        sideOffset={8}
        className='w-[340px] p-0'
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
      >
        <div className='flex flex-col gap-4 p-4'>
          {/* Header */}
          <div className='flex flex-col gap-0.5'>
            <div className='flex items-center justify-between gap-2'>
              <span className='text-sm font-semibold text-foreground'>
                {getTranslation('popover.title')}
              </span>
              {readyCount > 0 && (
                <Badge variant='warning'>
                  {getTranslation('stat.toReview', { count: readyCount })}
                </Badge>
              )}
            </div>
            <span className='text-xs text-muted-foreground'>{summaryLine}</span>
          </div>

          {/* In-progress jobs */}
          {inProgressItems.length > 0 && (
            <>
              <Separator />
              <div className='flex flex-col gap-3'>
                <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  {getTranslation('popover.inProgress')}
                </span>
                <div className='flex max-h-[140px] flex-col gap-4 overflow-y-auto'>
                  {inProgressItems.map(item => {
                    const fileName = readFileName(item);
                    const labelKey = JOB_TYPE_LABEL_KEYS[item.assetType];
                    const jobLabel = labelKey
                      ? getTranslation(labelKey)
                      : item.assetType;
                    const pct = item.percentage ?? 0;
                    return (
                      <div key={item.id} className='flex flex-col gap-2'>
                        <div className='flex items-center gap-3'>
                          <SectionIcon type='primary'>
                            <FontAwesomeIcon icon={faFile} />
                          </SectionIcon>
                          <div className='flex min-w-0 flex-col gap-0.5'>
                            <span className='truncate text-xs font-semibold text-foreground'>
                              {fileName}
                            </span>
                            <span className='text-xs uppercase leading-none tracking-wide text-muted-foreground'>
                              {jobLabel}
                            </span>
                          </div>
                        </div>
                        <div className='flex items-center gap-2'>
                          <Progress value={pct} className='flex-1' />
                          <span className='whitespace-nowrap text-xs text-muted-foreground'>
                            {getTranslation('popover.percentComplete', {
                              percentage: pct,
                            })}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Ready-to-review jobs */}
          {readyItems.length > 0 && (
            <>
              <Separator />
              <div className='flex flex-col gap-3'>
                <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  {getTranslation('popover.readyToReview')}
                </span>
                <div className='flex max-h-[132px] flex-col gap-4 overflow-y-auto'>
                  {readyItems.map(item => {
                    const fileName = readFileName(item);
                    const labelKey = READY_TYPE_LABEL_KEYS[item.assetType];
                    const readyLabel = labelKey
                      ? getTranslation(labelKey)
                      : item.assetType;
                    return (
                      <div key={item.id} className='flex items-center gap-3'>
                        <SectionIcon type='warning'>
                          <FontAwesomeIcon icon={faCircleCheck} />
                        </SectionIcon>
                        <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                          <span className='truncate text-xs font-semibold text-foreground'>
                            {fileName}
                          </span>
                          <span className='text-xs uppercase leading-none tracking-wide text-muted-foreground'>
                            {readyLabel}
                          </span>
                        </div>
                        <Badge variant='warning'>
                          {getTranslation('popover.readyToReview')}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Footer */}
          <div className='flex justify-end'>
            <Button size='sm' onClick={handleOpenQueue}>
              {getTranslation('popover.openQueue')}
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
